import React, { useState, useRef } from 'react';
import { FiSend } from 'react-icons/fi';
import ToneSelector from './ToneSelector';
import SuggestionChips from './SuggestionChips';

const LENGTH_OPTIONS = [
  { id: 'short', label: 'Short', icon: '⚡' },
  { id: 'medium', label: 'Medium', icon: '📝' },
  { id: 'long', label: 'Long', icon: '📄' }
];

const MODE_OPTIONS = [
  { id: 'reply', label: 'Generate Reply', icon: '✉️' },
  { id: 'fix', label: 'Fix My Draft', icon: '✏️' }
];

const LANGUAGES = [
  { id: 'auto', label: 'Auto-detect', flag: '🌐' },
  { id: 'en', label: 'English', flag: '🇺🇸' },
  { id: 'es', label: 'Spanish', flag: '🇪🇸' },
  { id: 'fr', label: 'French', flag: '🇫🇷' },
  { id: 'de', label: 'German', flag: '🇩🇪' },
  { id: 'hi', label: 'Hindi', flag: '🇮🇳' },
  { id: 'pt', label: 'Portuguese', flag: '🇧🇷' },
  { id: 'zh', label: 'Chinese', flag: '🇨🇳' },
  { id: 'ja', label: 'Japanese', flag: '🇯🇵' },
  { id: 'ko', label: 'Korean', flag: '🇰🇷' },
  { id: 'ar', label: 'Arabic', flag: '🇸🇦' },
  { id: 'ru', label: 'Russian', flag: '🇷🇺' },
  { id: 'it', label: 'Italian', flag: '🇮🇹' },
  { id: 'nl', label: 'Dutch', flag: '🇳🇱' },
  { id: 'tr', label: 'Turkish', flag: '🇹🇷' },
  { id: 'vi', label: 'Vietnamese', flag: '🇻🇳' },
  { id: 'th', label: 'Thai', flag: '🇹🇭' },
  { id: 'pl', label: 'Polish', flag: '🇵🇱' },
  { id: 'uk', label: 'Ukrainian', flag: '🇺🇦' },
  { id: 'sv', label: 'Swedish', flag: '🇸🇪' },
  { id: 'gu', label: 'Gujarati', flag: '🇮🇳' },
  { id: 'ta', label: 'Tamil', flag: '🇮🇳' },
  { id: 'bn', label: 'Bengali', flag: '🇮🇳' },
  { id: 'mr', label: 'Marathi', flag: '🇮🇳' }
];

const CATEGORIES = [
  { id: 'auto', label: 'Auto', icon: '🔄' },
  { id: 'job', label: 'Job/Career', icon: '💼' },
  { id: 'client', label: 'Client', icon: '🤝' },
  { id: 'support', label: 'Support', icon: '🎧' },
  { id: 'sales', label: 'Sales', icon: '💰' },
  { id: 'internal', label: 'Internal', icon: '🏢' },
  { id: 'personal', label: 'Personal', icon: '👤' },
  { id: 'complaint', label: 'Complaint', icon: '⚠️' },
  { id: 'networking', label: 'Networking', icon: '🔗' }
];

export default function MessageInput({ onSubmit, loading, disabled, onFocus }) {
  const [mode, setMode] = useState('reply');
  const [message, setMessage] = useState('');
  const [draft, setDraft] = useState('');
  const [tones, setTones] = useState(['professional']);
  const [replyInstructions, setReplyInstructions] = useState('');
  const [selectedChips, setSelectedChips] = useState([]);
  const [replyLength, setReplyLength] = useState('medium');
  const [replyLanguage, setReplyLanguage] = useState('auto');
  const [category, setCategory] = useState('auto');
  const textareaRef = useRef(null);

  const canSubmit = mode === 'reply' ? message.trim() : draft.trim();

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!canSubmit || loading) return;

    const lang = replyLanguage !== 'auto' ? replyLanguage : '';
    const cat = category !== 'auto' ? category : '';
    if (mode === 'fix') {
      onSubmit(message.trim(), tones, replyInstructions.trim(), replyLength, { mode: 'fix', draft: draft.trim(), language: lang, category: cat });
    } else {
      onSubmit(message.trim(), tones, replyInstructions.trim(), replyLength, { mode: 'reply', language: lang, category: cat });
    }

    setMessage('');
    setDraft('');
    setReplyInstructions('');
    setSelectedChips([]);
    if (textareaRef.current) textareaRef.current.style.height = 'auto';
  };

  const handleChipToggle = (chipText, adding) => {
    if (adding) {
      setSelectedChips(prev => [...prev, chipText]);
      setReplyInstructions(prev => prev ? `${prev}. ${chipText}` : chipText);
    } else {
      setSelectedChips(prev => prev.filter(c => c !== chipText));
      setReplyInstructions(prev => {
        return prev.replace(`. ${chipText}`, '').replace(chipText, '').replace(/^[.,\s]+|[.,\s]+$/g, '');
      });
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const autoResize = (e) => {
    const el = e.target;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 200) + 'px';
  };

  return (
    <div style={styles.wrapper}>
      <div style={styles.container}>
        {/* Mode Toggle */}
        <div style={styles.modeRow}>
          {MODE_OPTIONS.map(opt => (
            <button
              key={opt.id}
              onClick={() => setMode(opt.id)}
              style={{
                ...styles.modeBtn,
                ...(mode === opt.id ? styles.modeBtnActive : {})
              }}
            >
              <span>{opt.icon}</span>
              <span>{opt.label}</span>
            </button>
          ))}
        </div>

        <div style={styles.optionsRow}>
          <ToneSelector selected={tones} onChange={setTones} />
          <div style={styles.secondaryOptions}>
            <div style={styles.lengthSelector}>
              <span style={styles.lengthLabel}>Length:</span>
              <div style={styles.lengthOptions}>
                {LENGTH_OPTIONS.map(opt => (
                  <button
                    key={opt.id}
                    onClick={() => setReplyLength(opt.id)}
                    style={{
                      ...styles.lengthBtn,
                      ...(replyLength === opt.id ? styles.lengthBtnActive : {})
                    }}
                  >
                    <span>{opt.icon}</span>
                    <span>{opt.label}</span>
                  </button>
                ))}
              </div>
            </div>
            <div style={styles.languageSelector}>
              <span style={styles.lengthLabel}>Reply in:</span>
              <select
                value={replyLanguage}
                onChange={(e) => setReplyLanguage(e.target.value)}
                style={styles.languageSelect}
              >
                {LANGUAGES.map(lang => (
                  <option key={lang.id} value={lang.id}>
                    {lang.flag} {lang.label}
                  </option>
                ))}
              </select>
            </div>
            <div style={styles.languageSelector}>
              <span style={styles.lengthLabel}>Context:</span>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                style={styles.languageSelect}
              >
                {CATEGORIES.map(cat => (
                  <option key={cat.id} value={cat.id}>
                    {cat.icon} {cat.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Reply Instructions - only in reply mode */}
        {mode === 'reply' && (
          <div style={styles.instructionsWrapper}>
            <label style={styles.instructionsLabel}>Reply Instructions (optional)</label>
            <SuggestionChips message={message} onSelect={handleChipToggle} selectedChips={selectedChips} />
            <textarea
              value={replyInstructions}
              onChange={(e) => setReplyInstructions(e.target.value)}
              placeholder="e.g., Thank them, mention I attached the signed form, and ask if they need anything else..."
              rows={2}
              disabled={disabled}
              style={styles.instructionsTextarea}
            />
          </div>
        )}

        {/* Fix My Draft mode - draft input */}
        {mode === 'fix' && (
          <div style={styles.draftWrapper}>
            <label style={styles.draftLabel}>Your Draft</label>
            <textarea
              value={draft}
              onChange={(e) => { setDraft(e.target.value); autoResize(e); }}
              onKeyDown={handleKeyDown}
              placeholder="Paste your rough draft here — we'll polish it, fix grammar, and match your selected tone..."
              rows={4}
              disabled={disabled}
              style={styles.draftTextarea}
            />
          </div>
        )}

        {/* Main message input */}
        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.inputWrapper}>
            <textarea
              ref={textareaRef}
              value={message}
              onChange={(e) => { setMessage(e.target.value); autoResize(e); }}
              onKeyDown={handleKeyDown}
              onFocus={onFocus}
              placeholder={mode === 'fix'
                ? 'Paste the original email/message for context (optional)...'
                : 'Paste your email or message here...'
              }
              rows={mode === 'fix' ? 2 : 3}
              disabled={disabled}
              style={{
                ...styles.textarea,
                minHeight: mode === 'fix' ? 44 : 72
              }}
            />
            <button
              type="submit"
              disabled={!canSubmit || loading || disabled}
              style={{
                ...styles.sendBtn,
                ...(canSubmit && !loading ? { background: 'var(--accent)', color: 'white' } : {})
              }}
            >
              {loading ? (
                <div style={styles.spinner} />
              ) : (
                <FiSend size={18} />
              )}
            </button>
          </div>
        </form>
        <p style={styles.hint}>
          {mode === 'fix'
            ? 'Paste your draft above — the original email is optional for extra context'
            : 'Press Enter to send, Shift+Enter for new line'}
        </p>
      </div>
    </div>
  );
}

const styles = {
  wrapper: {
    padding: '16px 16px 24px',
    background: 'var(--bg-primary)'
  },
  container: {
    maxWidth: 800,
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    gap: 12
  },
  modeRow: {
    display: 'flex',
    gap: 8
  },
  modeBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '8px 16px',
    background: 'var(--bg-secondary)',
    border: '1px solid var(--border)',
    borderRadius: 10,
    color: 'var(--text-secondary)',
    fontSize: 13,
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.2s'
  },
  modeBtnActive: {
    background: '#6366f122',
    borderColor: 'var(--accent)',
    color: 'var(--accent)',
    fontWeight: 600
  },
  optionsRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: 10
  },
  form: { display: 'flex' },
  inputWrapper: {
    flex: 1,
    display: 'flex',
    alignItems: 'flex-end',
    gap: 8,
    background: 'var(--bg-secondary)',
    border: '1px solid var(--border)',
    borderRadius: 16,
    padding: '8px 12px',
    transition: 'border-color 0.2s'
  },
  textarea: {
    flex: 1,
    background: 'none',
    border: 'none',
    color: 'var(--text-primary)',
    fontSize: 15,
    lineHeight: 1.5,
    resize: 'none',
    outline: 'none',
    padding: '4px 0',
    minHeight: 72,
    maxHeight: 200
  },
  sendBtn: {
    width: 36,
    height: 36,
    borderRadius: '50%',
    background: 'var(--bg-tertiary)',
    border: 'none',
    color: 'var(--text-muted)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    flexShrink: 0,
    transition: 'all 0.2s'
  },
  spinner: {
    width: 18,
    height: 18,
    border: '2px solid transparent',
    borderTop: '2px solid white',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite'
  },
  hint: {
    fontSize: 12,
    color: 'var(--text-muted)',
    textAlign: 'center'
  },
  secondaryOptions: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    flexWrap: 'wrap'
  },
  lengthSelector: {
    display: 'flex',
    alignItems: 'center',
    gap: 8
  },
  languageSelector: {
    display: 'flex',
    alignItems: 'center',
    gap: 8
  },
  languageSelect: {
    padding: '5px 8px',
    background: 'var(--bg-primary)',
    border: '1px solid var(--border)',
    borderRadius: 16,
    color: 'var(--text-secondary)',
    fontSize: 12,
    cursor: 'pointer',
    outline: 'none'
  },
  lengthLabel: {
    fontSize: 13,
    color: 'var(--text-muted)',
    fontWeight: 500
  },
  lengthOptions: {
    display: 'flex',
    gap: 4
  },
  lengthBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    padding: '5px 10px',
    background: 'var(--bg-primary)',
    border: '1px solid var(--border)',
    borderRadius: 16,
    color: 'var(--text-secondary)',
    fontSize: 12,
    cursor: 'pointer',
    transition: 'all 0.2s',
    whiteSpace: 'nowrap'
  },
  lengthBtnActive: {
    background: '#6366f122',
    borderColor: 'var(--accent)',
    color: 'var(--accent)'
  },
  instructionsWrapper: {
    background: 'var(--bg-secondary)',
    border: '1px solid var(--accent)',
    borderRadius: 12,
    padding: '10px 14px',
    display: 'flex',
    flexDirection: 'column',
    gap: 6
  },
  instructionsLabel: {
    fontSize: 12,
    fontWeight: 600,
    color: 'var(--accent)',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  },
  instructionsTextarea: {
    background: 'none',
    border: 'none',
    color: 'var(--text-primary)',
    fontSize: 14,
    lineHeight: 1.5,
    resize: 'none',
    outline: 'none',
    padding: 0,
    minHeight: 40
  },
  draftWrapper: {
    background: 'var(--bg-secondary)',
    border: '1px solid #f59e0b',
    borderRadius: 12,
    padding: '10px 14px',
    display: 'flex',
    flexDirection: 'column',
    gap: 6
  },
  draftLabel: {
    fontSize: 12,
    fontWeight: 600,
    color: '#f59e0b',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  },
  draftTextarea: {
    background: 'none',
    border: 'none',
    color: 'var(--text-primary)',
    fontSize: 15,
    lineHeight: 1.5,
    resize: 'none',
    outline: 'none',
    padding: 0,
    minHeight: 80
  }
};
